var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var EncapsuleRules_1;
import { ArrayUtils, MGPFallible, MGPMap, MGPOptional } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { GameStatus } from '../../jscaip/GameStatus';
import { NInARowHelper } from '../../jscaip/NInARowHelper';
import { PlayerMap } from '../../jscaip/PlayerMap';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { Debug } from '../../utils/Debug';
import { MGPValidators } from '../../utils/MGPValidator';
import { EncapsuleFailure } from './EncapsuleFailure';
import { EncapsuleState, EncapsuleSpace, EncapsuleSizeToNumberMap } from './EncapsuleState';
export class EncapsuleNode extends GameNode {
}
let EncapsuleRules = class EncapsuleRules extends ConfigurableRules {
    static { EncapsuleRules_1 = this; }
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Encapsule`,
        config: {
            nInARow: new NumberConfig(3, RulesConfigDescriptionLocalizable.ALIGNMENT_SIZE, MGPValidators.range(1, 99)),
            width: new NumberConfig(3, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(3, 99)),
            height: new NumberConfig(3, RulesConfigDescriptionLocalizable.HEIGHT, MGPValidators.range(3, 99)),
            nbOfSizes: new NumberConfig(3, () => $localize `Number of different piece sizes`, MGPValidators.range(1, 8)),
            nbOfEachPiece: new NumberConfig(2, () => $localize `Number of pieces for each size`, MGPValidators.range(1, 9)),
        },
    });
    static get() {
        if (EncapsuleRules_1.singleton.isAbsent()) {
            EncapsuleRules_1.singleton = MGPOptional.of(new EncapsuleRules_1());
        }
        return EncapsuleRules_1.singleton.get();
    }
    getInitialState(config) {
        const _ = new EncapsuleSpace(new MGPMap());
        const startingBoard = TableUtils.create(config.width, config.height, _);
        const initialPieces = this.getInitialEncapsulePieceMap(config);
        return new EncapsuleState(startingBoard, 0, initialPieces, config.nbOfSizes);
    }
    getRulesConfigDescription() {
        return EncapsuleRules_1.RULES_CONFIG_DESCRIPTION;
    }
    getInitialEncapsulePieceMap(config) {
        const playerZeroPiecesNumber = ArrayUtils.create(config.nbOfSizes, config.nbOfEachPiece);
        const playerOnePiecesNumber = ArrayUtils.create(config.nbOfSizes, config.nbOfEachPiece);
        return this.getEncapsulePieceMapFrom(playerZeroPiecesNumber, playerOnePiecesNumber);
    }
    getEncapsulePieceMapFrom(playerZeroPiecesNumber, playerOnePiecesNumber) {
        const playerZero = this.getSizeToNumberMap(playerZeroPiecesNumber);
        const playerOne = this.getSizeToNumberMap(playerOnePiecesNumber);
        return PlayerMap.ofValues(playerZero, playerOne);
    }
    getSizeToNumberMap(nbOfEachPieces) {
        const map = new EncapsuleSizeToNumberMap();
        for (let i = 0; i < nbOfEachPieces.length; i++) {
            const size = i + 1;
            const nbOfEachPiece = nbOfEachPieces[i];
            map.set(size, nbOfEachPiece);
        }
        return map;
    }
    getVictoriousCoords(state, config) {
        const helper = new NInARowHelper((piece) => piece.getBiggest().getPlayer(), config.nInARow);
        return helper.getVictoriousCoord(state);
    }
    isVictory(state, config) {
        const victoriousCoords = this.getVictoriousCoords(state, config);
        if (victoriousCoords.length > 0) {
            const coord = victoriousCoords[0];
            return MGPOptional.of(state.getPieceAt(coord).getBiggest().getPlayer());
        }
        else {
            return MGPOptional.empty();
        }
    }
    isLegal(move, state) {
        let movingPiece;
        if (move.isDropping()) {
            movingPiece = move.piece.get();
            const owner = movingPiece.getPlayer();
            if (owner.isNone()) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
            }
            if (owner === state.getCurrentOpponent()) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
            }
            if (state.isInRemainingPieces(movingPiece) === false) {
                return MGPFallible.failure(EncapsuleFailure.PIECE_OUT_OF_STOCK());
            }
        }
        else {
            const startingCoord = move.startingCoord.get();
            const startingSpace = state.getPieceAt(startingCoord);
            movingPiece = startingSpace.getBiggest();
            const owner = movingPiece.getPlayer();
            if (owner.isNone()) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
            }
            if (owner === state.getCurrentOpponent()) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
            }
        }
        const landingSpace = state.getPieceAt(move.landingCoord);
        const superpositionResult = landingSpace.tryToSuperposePiece(movingPiece);
        if (superpositionResult.isPresent()) {
            return MGPFallible.success(superpositionResult.get());
        }
        return MGPFallible.failure(EncapsuleFailure.INVALID_PLACEMENT());
    }
    applyLegalMove(move, state, _config, newLandingSpace) {
        const newBoard = state.getCopiedBoard();
        const currentPlayer = state.getCurrentPlayer();
        const newRemainingPiecesMap = state.getRemainingPiecesCopy();
        const newRemainingPiece = newRemainingPiecesMap.get(currentPlayer).getCopy();
        const newTurn = state.turn + 1;
        newBoard[move.landingCoord.y][move.landingCoord.x] = newLandingSpace;
        let movingPiece;
        if (move.isDropping()) {
            movingPiece = move.piece.get();
            newRemainingPiece.add(movingPiece.size, -1);
            newRemainingPiecesMap.put(currentPlayer, newRemainingPiece);
        }
        else {
            const startingCoord = move.startingCoord.get();
            const oldStartingSpace = newBoard[startingCoord.y][startingCoord.x];
            const removalResult = oldStartingSpace.removeBiggest();
            newBoard[startingCoord.y][startingCoord.x] = removalResult.removedSpace;
            movingPiece = removalResult.removedPiece;
        }
        return new EncapsuleState(newBoard, newTurn, newRemainingPiecesMap, state.nbOfPieceSize);
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const winner = this.isVictory(state, config);
        if (winner.isPresent()) {
            return GameStatus.getVictory(winner.get());
        }
        else {
            return GameStatus.ONGOING;
        }
    }
};
EncapsuleRules = EncapsuleRules_1 = __decorate([
    Debug.log
], EncapsuleRules);
export { EncapsuleRules };
